import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {FormikHelpers} from 'formik';
import * as Yup from 'yup';
import {TShow} from '../../../../hooks/useShow';
import {RootState} from '../../../../redux/store.type';
import {TSlot, TTimetableItem} from '../../../../redux/timetable/timetable.type';
import {createBreak, updateBreak} from '../../../../redux/timetable/timetableActions';
import {combineDateTime, getStartOfDay} from '../../../../library/dates';
import {useTimetableDays} from '../../hooks/useTimetableDays';
import {useEventTimeRange} from '../../hooks/useEventTimeRange';
import {BreakFormValues} from './ManageBreakPopup';

const findTsid = (time: number | undefined, slots: TSlot[]): number | null => {
    if(!time) return null;
    const dayStart = getStartOfDay(time);
    return slots.find(s => combineDateTime(dayStart, s.timeStart) === time)?.tsid ?? null;
};

export const tsidToTime = (
    tsid: number | null,
    dayTimestamp: number,
    slots: TSlot[],
): number | undefined => {
    if(!tsid) return undefined;
    const slot = slots.find(s => s.tsid === tsid);
    return slot ? combineDateTime(dayTimestamp, slot.timeStart) : undefined;
};

const findLectureConflict = (
    dayLectures: TTimetableItem[],
    breakStart: number,
    selfTtid?: number,
) => dayLectures.find(l =>
    !l.isBreak && l.ttid !== selfTtid && l.time === breakStart,
);

const findBreakConflict = (
    dayLectures: TTimetableItem[],
    breakStart: number,
    selfTtid?: number,
) => dayLectures.find(l => l.isBreak && l.ttid !== selfTtid && l.time === breakStart);

const buildInitialValues = (
    breakItem: TTimetableItem | undefined,
    slots: TSlot[],
): BreakFormValues => ({
    title: breakItem?.title ?? '',
    tsid: findTsid(breakItem?.time, slots),
    description: breakItem?.description ?? '',
});

export function useBreakForm(show: TShow, breakItem: TTimetableItem | undefined) {
    const {t} = useTranslation('timetable');
    const slots = useSelector((state: RootState) => state.timetable.slots);
    const lecturesByDay = useSelector((state: RootState) => state.timetable.lectures);
    const {isWithin} = useEventTimeRange();
    const {conferenceDays, currentDayIndex} = useTimetableDays();
    const isEditing = !!breakItem;

    const dayTimestamp = isEditing
        ? getStartOfDay(breakItem!.time)
        : conferenceDays[currentDayIndex]?.timestamp;

    const initialValues = buildInitialValues(breakItem, slots);

    const validationSchema = useMemo(() => Yup.object({
        title:Yup.string().required(),
        tsid: Yup.number().nullable()
            .required(t('fieldRequired'))
            .test('noBreakConflict', t('breakAlreadyExists'), value => {
                if(!value || !dayTimestamp) return true;
                const time = tsidToTime(value, dayTimestamp, slots);
                if(!time) return true;
                const dayLectures = lecturesByDay[dayTimestamp] ?? [];
                return !findBreakConflict(dayLectures, time, breakItem?.ttid);
            })
            .test('noLectureConflict', t('lectureAtThisTime'), value => {
                if(!value || !dayTimestamp) return true;
                const time = tsidToTime(value, dayTimestamp, slots);
                if(!time) return true;
                const dayLectures = lecturesByDay[dayTimestamp] ?? [];
                return !findLectureConflict(dayLectures, time, breakItem?.ttid);
            }),
    }), [t, slots, lecturesByDay, dayTimestamp, breakItem?.ttid]);

    const handleSubmit = async (
        _changed: unknown,
        values: BreakFormValues,
        actions: FormikHelpers<BreakFormValues>,
    ): Promise<boolean> => {
        if(!dayTimestamp || values.tsid == null) return false;
        const time = tsidToTime(values.tsid, dayTimestamp, slots);
        if(time != null && !isWithin(time)) {
            actions.setFieldError('tsid', t('breakOutsideEventHours'));
            actions.setSubmitting(false);
            return false;
        }
        const payload = {
            title: values.title.trim() || undefined,
            description: values.description || undefined,
            tsid: values.tsid,
        };
        const result = isEditing
            ? await updateBreak(breakItem!.ttid, payload)
            : await createBreak({...payload, isBreak: true});
        return !!result;
    };

    return {initialValues, validationSchema, handleSubmit, dayTimestamp};
}
